import { Component, computed, inject, input, OnDestroy, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { switchMap, take, timer } from 'rxjs';
import { AssignmentService } from '../../../../service/assignment.service';
import { LossPreventionService } from '../../../../service/loss-prevention.service';

@Component({
  selector: 'app-review-timer',
  templateUrl: './review-timer.component.html',
  styleUrl: './review-timer.component.scss',
  standalone: false,
})
export class ReviewTimerComponent implements OnDestroy {
  private assignmentService = inject(AssignmentService);
  private lpService = inject(LossPreventionService);
  private titleService = inject(Title);

  reviewId = input.required<string>();
  disableExpiredModal = input<boolean>(false);

  private originalTitle = signal<string | undefined>(undefined);
  private expiresAt = signal<string | undefined>(undefined);

  // this prevents isExpiredOrNearExpiration from flickering
  // (cause user / timer hasn't finished loading)
  isInitialized = signal(false);
  private currentTime = signal(Date.now());

  private msRemaining = computed(() => {
    const expires = this.expiresAt();
    if (!expires) return 0;
    return new Date(expires).getTime() - this.currentTime();
  });

  isExpired = computed(() => this.msRemaining() < 0);

  timeLeftMilliseconds = computed(() => Math.abs(this.msRemaining()));

  displayTime = computed(() => {
    const absMs = this.timeLeftMilliseconds();
    const hours = Math.floor(absMs / (1000 * 60 * 60));
    const minutes = Math.floor((absMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((absMs % (1000 * 60)) / 1000);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  });

  isExpiredOrNearExpiration = computed(() => {
    if (!this.isInitialized()) return false;
    return (this.isExpired() && this.timeLeftMilliseconds() < 1000 * 60) || this.timeLeftMilliseconds() < 1000 * 60 * 40;
  });

  constructor() {
    // capture before the timer below can fire, so the tab title is restored correctly
    this.originalTitle.set(this.titleService.getTitle());

    // load the assignment once the required reviewId input is available; self-cleans on destroy
    toObservable(this.reviewId)
      .pipe(
        take(1),
        switchMap((reviewId) => this.assignmentService.getAssignment(reviewId)),
        takeUntilDestroyed(),
      )
      .subscribe((review) => {
        const expiresAt = review.expires_at;
        if (expiresAt !== null) {
          this.expiresAt.set(expiresAt);
          this.isInitialized.set(true);
        }
      });

    // tick every second to drive the countdown + tab title; self-cleans on destroy
    timer(0, 1000)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.currentTime.set(Date.now());

        const msRemaining = this.msRemaining();
        if (msRemaining <= 0) {
          this.titleService.setTitle(this.originalTitle() ?? '');
        } else {
          const hours = Math.floor(msRemaining / (1000 * 60 * 60));
          const minutes = Math.floor((msRemaining % (1000 * 60 * 60)) / (1000 * 60));
          const timerText = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
          this.titleService.setTitle(`(${timerText}) - ${this.originalTitle() ?? ''}`);
        }
      });
  }

  ngOnDestroy(): void {
    this.titleService.setTitle(this.originalTitle() ?? '');
  }

  renewReview() {
    this.lpService.renewReviewLease(this.reviewId()).subscribe((newExpiresAt) => {
      this.expiresAt.set(newExpiresAt);
    });
  }
}
